import {
  IPdfDefinitions,
  PdfContentType,
  IPdfData,
  PdfContentSetting,
  PdfSignature,
  INestedPdfContent,
  INestedPdfContentOptions,
  SEARCH_SUMMARY_DEFINE,
} from '@app/shared/common/make-pdf/v2';
import { ContentText } from 'pdfmake/interfaces';
import {
  groupByMajorMinorCategoryAndSort,
  generateUnitTextV2,
} from '@app/shared/utils/common-utils';
import { get, isNil, includes, isEqual, eq, lte, map } from 'lodash';
import { ExportExtrasOptions, ExportOptions } from './inventory-count.type';
import {
  getMajorCategoryName,
  getMinorCategoryName,
} from '@app/shared/utils/pdf-category.utils';
import { getItemNameWidth } from './inventory-count.util';
import { IMeasure } from '@app/shared/types';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';

const INVENTORY_COUNT_PDF_COMMON_SIGNATURES: PdfSignature[] = [
  I18n.Common.Countedby,
  I18n.Common.Recordedby,
  I18n.Common.Enteredby,
];

const buildInventoryCountPdfConfig = (
  data: IPdfDefinitions['data'],
  options: ExportOptions & {
    bulkOptions?: IPdfDefinitions['bulkOptions'];
    i18nService: XVII18nService;
  },
  extrasOptions: ExportExtrasOptions,
): IPdfDefinitions => ({
  data,
  bulkOptions: options.bulkOptions,
  generateFileName: (): string => '',
  generateHeader: (): ContentText[] => {
    return [
      {
        text: options?.i18nService.instant(
          I18n.EntityNames.InventoryCountSheet,
        ),
        fontSize: 14,
        bold: true,
        margin: [20, 20, 20, 0],
      },
    ];
  },
  content: {
    mainGrids: {
      type: PdfContentType.Grids,
      setting: (pdfData: IPdfData): PdfContentSetting => {
        const { siteInfo, inventoryCountName } = pdfData;
        const isAlphabetical = options.mode === 'alphabetical';
        const { columnsHideIds } = options;
        const itemNameWidth = getItemNameWidth(isAlphabetical, columnsHideIds);

        return {
          'virtual.inventory_count_items': {
            title: ({ gridData }): string => {
              return isAlphabetical
                ? null
                : `${options.i18nService.instant(I18n.Forms.GridForm.MajorCategory)}: ${getMajorCategoryName(gridData[0])} - ${options.i18nService.instant(I18n.Forms.GridForm.MinorCategory)}: ${getMinorCategoryName(
                    gridData[0],
                  )}`;
            },
            orderBy: (): string[] | null =>
              !extrasOptions.isFromDetail ? ['name asc'] : null,
            multiGrid: (params): any =>
              includes(['major category', 'consolidate category'], options.mode)
                ? groupByMajorMinorCategoryAndSort(params?.data)
                : null,
            columns: [
              {
                path: 'name',
                title: I18n.Common.Item,
                width: itemNameWidth,
              },
              {
                path: 'reporting_category_major.name',
                title: I18n.Forms.GridForm.MajorCategory,
                width: '15%',
                hiddenColumn:
                  !isAlphabetical ||
                  includes(columnsHideIds, 'reporting_category_major.name'),
              },
              {
                path: 'reporting_category_minor.name',
                title: I18n.Forms.GridForm.MinorCategory,
                width: '15%',
                hiddenColumn:
                  !isAlphabetical ||
                  includes(columnsHideIds, 'reporting_category_minor.name'),
              },
              {
                path: 'uomDes_0',
                title: I18n.Common.UOM,
                width: isAlphabetical ? '11.6%' : '18.6%',
                valueGetter: ({ tools, ...params }): string => {
                  if (isNil(params.value)) {
                    return null;
                  }

                  return generateUnitTextV2(
                    {
                      description: params.value ?? '',
                      factor_value: get(params.rowData, 'uomFactorValue_0', ''),
                    } as IMeasure,
                    {
                      numberFormatService: tools.services.numberFormatService,
                      notNeedAddSign: true,
                    },
                  );
                },
              },
              {
                path: 'uomCount_0',
                title: I18n.Common.Count,
                width: isAlphabetical ? '5%' : '8%',
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        false,
                        undefined,
                        {
                          precision: 3,
                        },
                      )(params),
              },
              {
                path: 'uomDes_1',
                title: I18n.Common.UOM,
                width: isAlphabetical ? '11.6%' : '18.6%',
                valueGetter: ({ tools, ...params }): string => {
                  if (isNil(params.value)) {
                    return null;
                  }

                  return generateUnitTextV2(
                    {
                      description: params.value ?? '',
                      factor_value: get(params.rowData, 'uomFactorValue_1', ''),
                    } as IMeasure,
                    {
                      numberFormatService: tools.services.numberFormatService,
                      notNeedAddSign: true,
                    },
                  );
                },
              },
              {
                path: 'uomCount_1',
                title: I18n.Common.Count,
                width: isAlphabetical ? '5%' : '8%',
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        false,
                        undefined,
                        {
                          precision: 3,
                        },
                      )(params),
              },
              {
                path: 'uomDes_2',
                title: I18n.Common.UOM,
                width: isAlphabetical ? '11.6%' : '18.6%',
                valueGetter: ({ tools, ...params }): string => {
                  if (isNil(params.value)) {
                    return null;
                  }

                  return generateUnitTextV2(
                    {
                      description: params.value ?? '',
                      factor_value: get(params.rowData, 'uomFactorValue_2', ''),
                    } as IMeasure,
                    {
                      numberFormatService: tools.services.numberFormatService,
                      notNeedAddSign: true,
                    },
                  );
                },
              },
              {
                path: 'uomCount_2',
                title: I18n.Common.Count,
                width: isAlphabetical ? '5%' : '8%',
                valueFormatter: ({ tools, ...params }): string =>
                  isNil(params.value)
                    ? ''
                    : tools.services.numberFormatService.getNumberFormatter(
                        false,
                        undefined,
                        {
                          precision: 3,
                        },
                      )(params),
              },
            ],
            nestedContent: (
              _: IPdfData,
              nestedOptions: INestedPdfContentOptions,
            ): INestedPdfContent => {
              const isMajorCategoryMode = isEqual(
                options.mode,
                'major category',
              );
              const getIsSameWithNextMajorCategory = (): boolean =>
                isEqual(
                  getMajorCategoryName(nestedOptions.curGridData?.[0]),
                  getMajorCategoryName(nestedOptions.nextGridData?.[0]),
                );

              const getIsSameWithPreMajorCategory = (): boolean =>
                isEqual(
                  getMajorCategoryName(nestedOptions.preGridData?.[0]),
                  getMajorCategoryName(nestedOptions.curGridData?.[0]),
                );

              const gridCounts = nestedOptions.multiGridCount ?? 1;
              const multiGridBuildIndex =
                nestedOptions.multiGridBuildIndex ?? 0;

              const isEmptyOrSingleGrids = lte(gridCounts, 1);

              // show signature condition
              // 1: is empty or single grids
              // 2: is major category export and next grid category is not same with current grid
              // 3: is last page of this whole grid
              const showSignature =
                isEmptyOrSingleGrids ||
                (isMajorCategoryMode && !getIsSameWithNextMajorCategory()) ||
                eq(multiGridBuildIndex, gridCounts - 1);

              // show content title condition
              // 1: is empty or single grids
              // 2: is major category export and pre grid category is not same with current grid
              // 3: is first page of this whole grid
              const showContentTitle =
                isEmptyOrSingleGrids ||
                (isMajorCategoryMode && !getIsSameWithPreMajorCategory()) ||
                eq(multiGridBuildIndex, 0);

              return {
                before: {
                  contentTitle: showContentTitle && {
                    type: PdfContentType.SimpleText,
                    setting: {
                      text: `${options.i18nService.instant(I18n.Common.Location)}: ${siteInfo.store_number} - ${siteInfo.name}                             ${inventoryCountName}                        ${options.i18nService.instant(I18n.Common.Initials)}:`,
                      style: 'title',
                      margin: [0, 0, 0, 5],
                    },
                  },
                  summary: showContentTitle && {
                    type: PdfContentType.Summary,
                    setting: {
                      fields: [SEARCH_SUMMARY_DEFINE],
                    },
                  },
                },
                after: {
                  signatures: showSignature && {
                    type: PdfContentType.Signatures,
                    setting: map(
                      INVENTORY_COUNT_PDF_COMMON_SIGNATURES,
                      (i18nKey: I18n.Common) =>
                        options.i18nService.instant(i18nKey),
                    ),
                    pageBreak: 'after',
                  },
                },
              };
            },
          },
        };
      },
    },
  },
  options: {
    pageMargins: [20, 50, 20, 60],
    defaultEqualColumnWidth: true,
  },
});

export { buildInventoryCountPdfConfig };
